import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../features/userSlice";
import {
  BizDevelopmentDashboardEndPointLogic,
  OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES,
  OverviewAccountSummaryTimeframe,
} from "../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import {
  setComparisonQueryOutput,
  setComparisonFetchedFor,
} from "../../../../features/bizDevelopmentDashboardSlice";
import store from "../../../../store";
import { BIZ_DEVELOPMENT_BUDGET_QUERY_ID } from "../QueryIDsBizDevelopmentDashboard";

export const useBizDevelopmentDashboard = () => {
  const [searchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const oauthTokenHeader = userData?.data?.oauth_token;

  const type = "site"; // statically set as per current requirements
  const dimension = ["bbb_uk"]; // statically set as per current requirements
  const queryId: string = BIZ_DEVELOPMENT_BUDGET_QUERY_ID;

  const getSitesFromParams = (): string[] => {
    const sites = searchParams.get("sites");
    return sites ? sites.split(",").filter(Boolean) : [];
  };

  const getTimeframeFromParams = (): string => {
    return searchParams.get("timeframePerformance") || "yesterday";
  };

  const getOverviewAccountSummaryTimeframeFromParams =
    (): OverviewAccountSummaryTimeframe => {
      return (searchParams.get("timeframeOverviewAccountSummary") ||
        OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastThirtyDays) as OverviewAccountSummaryTimeframe;
    };

  const fetchDashboardData = async (
    timeframe?: string,
    siteSharedUids?: string[],
  ) => {
    const resolvedTimeframe = timeframe ?? getTimeframeFromParams();
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    await BizDevelopmentDashboardEndPointLogic.getDashboardData(
      oauthTokenHeader,
      resolvedSites,
      resolvedTimeframe,
    );
  };

  const fetchComparisonData = async (
    timeframe: string,
    siteSharedUids?: string[],
  ) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;

    await BizDevelopmentDashboardEndPointLogic.getComparisonDashboardData(
      oauthTokenHeader,
      resolvedSites,
      timeframe,
    );
  };

  const clearComparisonData = () => {
    store.dispatch(setComparisonQueryOutput([]));
    store.dispatch(setComparisonFetchedFor(null));
  };

  const fetchBudgetData = async (siteSharedUids?: string[]) => {
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getBizDevelopmentBudgetData({
      oauthTokenHeader,
      queryId,
      websiteSharedUids: resolvedSites,
      type,
      dimension,
    });
  };

  const fetchOverviewAccountSummaryData = async (
    timeframe?: OverviewAccountSummaryTimeframe,
    siteSharedUids?: string[],
  ) => {
    const resolvedTimeframe =
      timeframe ?? getOverviewAccountSummaryTimeframeFromParams();
    const resolvedSites = siteSharedUids ?? getSitesFromParams();
    if (resolvedSites.length === 0) return;
    await BizDevelopmentDashboardEndPointLogic.getOverviewAccountSummaryData(
      oauthTokenHeader,
      resolvedSites,
      resolvedTimeframe,
    );
  };

  return {
    fetchDashboardData,
    fetchComparisonData,
    fetchOverviewAccountSummaryData,
    clearComparisonData,
    getSitesFromParams,
    getTimeframeFromParams,
    fetchBudgetData,
    getOverviewAccountSummaryTimeframeFromParams,
  };
};
